import { useEffect, useLayoutEffect, useState } from 'react'
import { Link, NavLink, Outlet, ScrollRestoration, useLocation } from 'react-router'
import { useMe, useTasks } from '../api/hooks.ts'
import { FallbackBottomBar } from '../telegram/buttons.tsx'
import { haptic } from '../telegram/haptics.ts'
import { isRootPath, useTelegramBackButton } from '../telegram/navigation.ts'
import { getTelegramEnv } from '../telegram/sdk.ts'
import { IconPlus, IconTabMine, IconTabMore, IconTabOutbox, IconTabTeam } from './icons.tsx'
import { Sidebar } from './Sidebar.tsx'
import { SnackbarHost } from './Snackbar.tsx'

/** Широкий экран (компьютер): боковое меню вместо нижних вкладок. */
const WIDE_QUERY = '(min-width: 960px)'

function useWide(): boolean {
  const [wide, setWide] = useState(() => window.matchMedia(WIDE_QUERY).matches)
  useEffect(() => {
    const mq = window.matchMedia(WIDE_QUERY)
    const on = () => setWide(mq.matches)
    mq.addEventListener('change', on)
    return () => mq.removeEventListener('change', on)
  }, [])
  return wide
}

function TabBar({
  isManager,
  mineBadge,
  outboxBadge,
}: {
  isManager: boolean
  mineBadge: number
  outboxBadge: number
}) {
  useLayoutEffect(() => {
    document.documentElement.style.setProperty('--ovo-tabbar-h', '57px')
    return () => document.documentElement.style.setProperty('--ovo-tabbar-h', '0px')
  }, [])

  const tabs = [
    { to: '/', label: 'Мои', icon: <IconTabMine size={26} />, badge: mineBadge, end: true },
    { to: '/outbox', label: 'Поставил', icon: <IconTabOutbox size={26} />, badge: outboxBadge, end: false },
    ...(isManager
      ? [{ to: '/team', label: 'Команда', icon: <IconTabTeam size={26} />, badge: 0, end: false }]
      : []),
    { to: '/more', label: 'Ещё', icon: <IconTabMore size={26} />, badge: 0, end: false },
  ]

  return (
    <nav className="tabbar" aria-label="Разделы">
      <div className="tabbar__inner">
        {tabs.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            end={t.end}
            replace
            className={({ isActive }) => `tabbar__item${isActive ? ' tabbar__item--active' : ''}`}
            onClick={() => haptic.selection()}
          >
            <span className="tabbar__icon">{t.icon}</span>
            <span>{t.label}</span>
            {t.badge > 0 && (
              <span className="tabbar__badge" role="img" aria-label={`${t.badge} требуют внимания`}>
                {t.badge > 99 ? '99+' : t.badge}
              </span>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  )
}

function Fab() {
  return (
    <div className="fab-wrap">
      <div className="fab-wrap__inner">
        <Link
          to="/tasks/new"
          className="fab"
          aria-label="Поставить задачу"
          onClick={() => haptic.impact('medium')}
        >
          <IconPlus size={28} strokeWidth={2.2} />
        </Link>
      </div>
    </div>
  )
}

function DevThemeToggle() {
  const [mode, setMode] = useState<string>('system')
  useEffect(() => {
    void import('../telegram/mockEnv.ts').then((m) => setMode(m.getDevTheme()))
  }, [])
  const labels: Record<string, string> = {
    system: 'как в системе',
    dark: 'тёмная',
    light: 'светлая',
  }
  // только в dev-имитации Telegram: переключение темы по кругу
  return (
    <button
      type="button"
      className="dev-toggle"
      title={`Тема: ${labels[mode] ?? mode}`}
      aria-label={`Сменить тему (сейчас: ${labels[mode] ?? mode})`}
      onClick={() => {
        void import('../telegram/mockEnv.ts').then((m) => setMode(m.cycleDevTheme()))
      }}
    >
      {mode === 'dark' ? '☾' : mode === 'light' ? '☀' : '◐'}
    </button>
  )
}

/** Каркас: экран + таббар и «+» на корневых экранах, нижняя панель кнопок, снекбар. */
export function AppShell() {
  const { pathname } = useLocation()
  const root = isRootPath(pathname)
  useTelegramBackButton()
  const me = useMe()
  // бейджи: ждут принятия у меня и сданные мне на проверку
  const mine = useTasks({ scope: 'mine' }, Boolean(me.data))
  const outbox = useTasks({ scope: 'created' }, Boolean(me.data))
  const meId = me.data?.id ?? 0
  const mineBadge = (mine.data ?? []).filter((t) => t.status === 'new' && t.assignee.id === meId).length
  const outboxBadge = (outbox.data ?? []).filter(
    (t) => t.status === 'submitted' && t.assignee.id !== meId,
  ).length

  const wide = useWide()

  return (
    <div className={`app-shell${wide ? ' app-shell--wide' : ''}`}>
      {wide && <Sidebar mineBadge={mineBadge} outboxBadge={outboxBadge} />}
      <div className="app-main">
        <Outlet />
      </div>
      {import.meta.env.DEV && getTelegramEnv().mocked && root && <DevThemeToggle />}
      {root && !wide && (
        <>
          <Fab />
          <TabBar isManager={Boolean(me.data?.isManager)} mineBadge={mineBadge} outboxBadge={outboxBadge} />
        </>
      )}
      <FallbackBottomBar />
      <SnackbarHost />
      <ScrollRestoration />
    </div>
  )
}
